/* eslint-disable eslint-comments/disable-enable-pair -- needed */
/* eslint-disable no-unsafe-optional-chaining -- needed */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ExtendReactlytics } from '@americanexpress/reactlytics';
import {
  ComponentLevelNotification,
  Button,
  Checkbox,
  Input,
  Heading,
} from '@americanexpress/dls-react';
import CardMemberInfo from '../../CardMemberInfo';

import { Layout } from '../layout';
import { renderHTML } from '../../../../../utilities/render-html';
import useOneData from '../../../../../hooks/useOneData';
import useDisableButton from '../../../../../hooks/useDisableButton';
import {
  getEndpoint,
  getEnrollmentRequestBody,
} from '../../../../../api/api-utils';
import { CreateLoyaltyBenefitEnrollmentStatusVx } from '../../../../../constants';

export function MembershipInputApply(props) {
  const [checkbox, toggleCheckBox] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [agreeError, setAgreeError] = useState(false);
  const [inputWarning, setInputWarning] = useState('');
  const [success, setSuccess] = useState(false);
  const intl = useIntl(); // Hook for accessing locale content, intl.formatMessage()
  const stepTransition = props.stepTransition;
  const selectedProduct = props.selectedProduct;
  const inputctatext = props.inputctatext;
  const label = props.callToAction?.INTERNAL.label || props.callToAction?.ANCHOR?.label || 'Enroll Now';
  const accountField = props?.requiredFields?.account?.memberAccount;
  const accountType = accountField?.type;
  const accountMinLength = accountField?.minLength || 0;
  const accountMaxLength = accountField?.maxLength || 19;
  const invalidCharWarning = accountType === 'number' ? 'Please enter numbers only.' : 'Please enter letters and numbers only.';
  const invalidLengthWarning = `Enter a ${accountMinLength}-${accountMaxLength} digit account number.`;

  const languageData = props?.languageData;
  const description = props?.description;
  const { isFetching, error, data } = props?.cardData;
  const safeDescription = renderHTML(description);

  const faasApiUrl = props?.faasApiUrl;
  const sorBenefitId = props.sorBenefitId;
  const benefitName = props.benefitName;
  const cardname = props.cardname;
  const benefitTitle = props?.benefitTitle;
  const endpoint = getEndpoint(
    faasApiUrl,
    CreateLoyaltyBenefitEnrollmentStatusVx
  );
  const locale = props?.locale;
  const requestOptions = {
    membershipNumber: inputValue,
    benefitName,
    cardname,
  };
  const requestBody = getEnrollmentRequestBody(
    selectedProduct,
    sorBenefitId,
    locale,
    requestOptions
  );
  const parseEntities = (txt) => new DOMParser().parseFromString(txt, 'text/html').body.textContent;

  const endpointOptions = {
    body: requestBody,
    defer: true, // defers fetchye call until run function is invoked
  };
  const { isLoading, run } = useOneData(endpoint, endpointOptions);
  const { isDisabledAfterSubmit, disableButton } = useDisableButton();

  const validate = (input = '') => {
    // In BAU, we allow card member(CM) to enroll without inputting membership number. When the CM does decide to input membership number, we apply validation checks to see if there is an invalid character or length (greater than 20). If there is, we display a warning message and disable the enroll submit button.
    const inputLength = input.length;
    const validateRegex = accountType === 'number' ? /^\d+$/ : /^\w+$/;
    const isInvalidRegexInput = !validateRegex.test(input);
    // Check is ported from legacy and kept this way to maintain business as usual. Check ensures 20+ chars.
    const invalidLength = inputLength < accountMinLength || inputLength > accountMaxLength;

    if (isInvalidRegexInput) {
      // invalid char
      setInputWarning(invalidCharWarning);
      return false;
    }

    if (invalidLength) {
      setInputWarning(invalidLengthWarning);
      return false;
    }

    setInputWarning('');
    return true;
  };

  // eslint-disable-next-line consistent-return -- needed
  const submit = async () => {
    const isValidSubmit = checkbox;
    const showAgreeError = !checkbox;

    if (showAgreeError) {
      return setAgreeError(true);
    }

    if (isValidSubmit) {
      setAgreeError(false);
      disableButton();
      const { data: enrollmentSuccess, error: enrollmentError } = await run();
      if (enrollmentSuccess) {
        stepTransition(true, false);
      }
      if (enrollmentError) {
        stepTransition(false, true);
      }
    }
  };

  const onChangeInput = (value) => {
    const valid = validate(value);
    setSuccess(valid);
    setInputValue(value);
  };

  const onChangeCheckbox = () => {
    if (!checkbox) {
      if (agreeError) {
        setAgreeError(false);
      }
      toggleCheckBox(true);
    } else {
      toggleCheckBox(false);
    }
  };

  return (
    <>
      <section className="pad-2-lr text-justify ">
        <Heading level={2} variant="sans-small-regular" className="text-align-center margin-0 margin-3-b">
          {renderHTML(benefitTitle)}
        </Heading>
        {safeDescription && <article className="margin-3-b">{safeDescription}</article>}
      </section>
      <Layout>
        {!isFetching && !error && (
          <CardMemberInfo {...data} languageData={languageData} />
        )}
        <section className="margin-t">
          <section className="pad-2-b">
            <Input
              id="membershipNumber"
              label={inputctatext}
              onChange={(e) => onChangeInput(e.target.value)}
              success={success}
              status={inputWarning ? 'error' : 'default'}
            />
            {inputWarning && (
              <ComponentLevelNotification className="pad-1-t">
                {inputWarning}
              </ComponentLevelNotification>
            )}
          </section>
          {intl.formatMessage({
            id: 'hide-ui.tnc-checkbox',
            defaultMessage: '',
          }) === 'true'
            ? ''
            : (
              <Checkbox
                id="enrollment-checkbox"
                label={`${intl.formatMessage({ id: 'shared.terms-links.terms-and-conditions', defaultMessage: 'I understand and agree to the terms and conditions.‡' })}`}
                reactlytics={{
                  element: `${cardname}>>${benefitName}>>agree-to-terms`,
                }}
                onChange={onChangeCheckbox}
              />
            )}
        </section>
        {agreeError && (
        <ComponentLevelNotification className="pad-2-t">
          Please read and agree to the Terms & Conditions.
        </ComponentLevelNotification>
        )}
        <ExtendReactlytics
          analytics={{
            event: {
              meta: {
                benefitname: `${parseEntities(benefitTitle)?.replace(/ /g, '-').toLowerCase()}`,
              },
            },
          }}
        >
          <Button
            id="enrollsubmit"
            className="flex flex-column flex-align-items-center margin-auto-lr margin-3-tb"
            reactlytics={{
              element: `cta>>enroll-submit>>${cardname?.replace(/ /g, '-').toLowerCase()}`,
              type: 'impression',
            }}
            onClick={submit}
            aria-disabled={
              inputWarning !== '' ? inputWarning : isDisabledAfterSubmit
            }
            working={isLoading}
            aria-label={`${`${intl.formatMessage({ id: label })} ${benefitName}`}`}
            variant="primary"
          >
            {intl.formatMessage({
              id: `shared.terms-links.${label.replace(/ /g, '-').toLowerCase()}`,
              defaultMessage: label,
            })}
          </Button>
        </ExtendReactlytics>
      </Layout>
    </>
  );
}
